// Sources page (RGS-5) — every source AI cited for one report month, filterable
// and groupable, with a per-source detail drawer. Reached from the dashboard
// reputation card ("Open sources →"), the sidebar "Sources" entry, or directly.
//
// Single-month-bound (the route carries the reportId = one calendar month); the
// in-page period dropdown switches months by navigating between report ids. The
// `?orbit=` deep-link from the card pre-filters and round-trips to the URL.
// Paid-only: free users are bounced to billing (the endpoint would 403).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useReportSources } from "@/hooks/vb/useReportSources";
import { useReportPeriods } from "@/hooks/vb/useReportPeriods";
import { useIsMobile } from "@/hooks/useIsMobile";
import { OrbitBar, ORBIT_ORDER, ORBIT_META, reputationLede } from "@/components/vb/OrbitBar";
import { Input, Segmented } from "@/components/vb";
import type { Orbit } from "@/types/vb";
import { SourcesTable } from "./SourcesTable";
import { SourceDrawer } from "./SourceDrawer";
import { PeriodMenu } from "./PeriodMenu";
import { FilterMenu, type FilterOption } from "./FilterMenu";
import { KIND_LABELS, kindKey, presentKindKeys } from "./sourcesUtil";

const ORBIT_PARAMS: Orbit[] = ["owned", "influenced", "earned"];

export function SourcesPage() {
  const { projectId, reportId } = useParams<{ projectId: string; reportId?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: periods } = useReportPeriods(projectId);

  // Reached without a reportId (sidebar entry): resolve the latest report and
  // redirect to the canonical URL; no reports at all → back to the dashboard.
  useEffect(() => {
    if (reportId || !periods) return;
    if (periods.length === 0) navigate(`/reports/${projectId}`, { replace: true });
    else navigate(`/reports/${projectId}/${periods[0].id}/sources`, { replace: true });
  }, [reportId, periods, projectId, navigate]);

  const numericReportId = reportId ? Number(reportId) : undefined;
  const { data, isLoading } = useReportSources(numericReportId, true);

  // Orbit filter ↔ URL (the one filter worth sharing / deep-linking).
  const orbitParam = searchParams.get("orbit");
  const orbit: Orbit | "all" =
    orbitParam && (ORBIT_PARAMS as string[]).includes(orbitParam) ? (orbitParam as Orbit) : "all";
  const setOrbit = (next: Orbit | "all") => {
    const p = new URLSearchParams(searchParams);
    if (next === "all") p.delete("orbit");
    else p.set("orbit", next);
    setSearchParams(p, { replace: true });
  };
  const toggleOrbit = (o: Orbit) => setOrbit(orbit === o ? "all" : o);

  const [type, setType] = useState("all");
  const [signal, setSignal] = useState("all");
  // Seed the domain filter from ?domain= so a "Top domain" badge on the report
  // card lands here with that domain already filtered.
  const [search, setSearch] = useState(() => searchParams.get("domain") ?? "");
  const [grouped, setGrouped] = useState(true);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Grouped sources default to expanded on desktop but collapsed on mobile
  // (the stacked card layout gets long fast). Toggling overrides per domain.
  const defaultExpanded = !useIsMobile();
  const [openId, setOpenId] = useState<string | null>(null);
  // Stable so the drawer's keydown/focus effect doesn't re-subscribe (and steal
  // focus back to its close button) on every unrelated parent re-render.
  const closeDrawer = useCallback(() => setOpenId(null), []);

  const sources = useMemo(() => data?.sources ?? [], [data]);

  const orbitOptions = useMemo<FilterOption[]>(() => {
    const count = (o: Orbit) => sources.filter((s) => s.orbit === o).length;
    return [
      { value: "all", label: `All orbits · ${sources.length}` },
      ...ORBIT_ORDER.map((o) => ({ value: o, label: `${ORBIT_META[o].tech} · ${count(o)}` })),
    ];
  }, [sources]);
  const typeFilterOptions = useMemo<FilterOption[]>(
    () => [
      { value: "all", label: "All types" },
      ...presentKindKeys(sources).map((k) => ({ value: k, label: KIND_LABELS[k] })),
    ],
    [sources],
  );
  const signalFilterOptions = useMemo<FilterOption[]>(() => {
    const set = new Set<string>();
    sources.forEach((s) => s.signals.forEach((sig) => set.add(sig)));
    return [
      { value: "all", label: "All signals" },
      ...[...set].sort((a, b) => a.localeCompare(b)).map((s) => ({ value: s, label: s })),
    ];
  }, [sources]);

  const rows = useMemo(
    () =>
      sources.filter((s) => {
        if (orbit !== "all" && s.orbit !== orbit) return false;
        if (type !== "all" && kindKey(s.kind) !== type) return false;
        if (signal !== "all" && !s.signals.includes(signal)) return false;
        if (search && !s.display_url.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
      }),
    [sources, orbit, type, signal, search],
  );

  const anyFilter = orbit !== "all" || type !== "all" || signal !== "all" || search !== "";
  const clearFilters = () => {
    setOrbit("all");
    setType("all");
    setSignal("all");
    setSearch("");
  };

  const toggleGroup = (domain: string) =>
    setExpanded((prev) => ({ ...prev, [domain]: !(prev[domain] ?? defaultExpanded) }));

  const openRow = useMemo(
    () => (openId ? sources.find((s) => s.id === openId) ?? null : null),
    [openId, sources],
  );

  // Redirecting (free → billing, or no reportId → canonical month / dashboard).
  if (!reportId) return null;

  const summary = data?.summary;
  const orbitCount = (o: Orbit) => sources.filter((s) => s.orbit === o).length;

  // Deterministic lede keyed to the dominant orbit — the same helper the dashboard
  // ReputationCard uses, so the two readouts stay in lockstep.
  const lede = summary ? reputationLede(summary.orbits) : "";

  return (
    <div className="vb-root src-page">
      <div className="src-page-hd">
        <h1>Sources</h1>
        {periods && periods.length > 0 && (
          <PeriodMenu
            periods={periods}
            currentId={numericReportId}
            onSelect={(id) => {
              // Keep the active orbit filter across the month switch.
              const qs = searchParams.toString();
              navigate(`/reports/${projectId}/${id}/sources${qs ? `?${qs}` : ""}`);
            }}
          />
        )}
      </div>

      {isLoading && !data ? (
        <div className="src-stack">
          <div className="src-skel src-skel--readout" />
          <div className="src-skel src-skel--table" />
        </div>
      ) : !summary || summary.citations_count === 0 ? (
        <div className="src-blank">No sources captured for this report yet.</div>
      ) : (
        <div className="src-stack">
          {/* ---- Readout (ownership bar — click a segment to filter) ---- */}
          <section className="src-readout" aria-label="Where AI gets its picture of you">
            <div className="src-readout-meta">
              <span className="l">Where AI gets its picture of you</span>
              <span className="r tnum">
                {summary.period} · {summary.sources_count} sources · {summary.citations_count} citations
              </span>
            </div>
            <p className="src-readout-lede">{lede}</p>
            <OrbitBar orbits={summary.orbits} activeOrbit={orbit} onSelect={toggleOrbit} />
            <span className="src-hint">Click a segment of the bar to filter the list below.</span>
          </section>

          {/* ---- Filters ---- */}
          <div className="src-filters" role="group" aria-label="Filter sources">
            <span className="src-fl">Orbit</span>
            {/* Desktop: orbit chips. Mobile: a dropdown (below) — chips wrap too wide. */}
            <div className="src-orbit-chips">
              <button
                type="button"
                className={`vb-btn vb-btn--sm src-chip${orbit === "all" ? " on" : ""}`}
                onClick={() => setOrbit("all")}
              >
                All · {sources.length}
              </button>
              {ORBIT_ORDER.map((o) => (
                <button
                  key={o}
                  type="button"
                  className={`vb-btn vb-btn--sm src-chip${orbit === o ? " on" : ""}`}
                  onClick={() => setOrbit(o)}
                >
                  <span className={`src-chip-dot src-orbit-dot--${o}`} aria-hidden="true" />
                  {ORBIT_META[o].tech} · {orbitCount(o)}
                </button>
              ))}
            </div>
            {/* Mobile only — orbit as a dropdown (chips wrap too wide). */}
            <FilterMenu
              className="src-orbit-menu"
              ariaLabel="Filter by orbit"
              value={orbit}
              options={orbitOptions}
              onChange={(v) => setOrbit(v === "all" ? "all" : (v as Orbit))}
            />

            <span className="src-fl src-fl--gap">Type</span>
            <FilterMenu
              ariaLabel="Filter by source type"
              value={type}
              options={typeFilterOptions}
              onChange={setType}
            />

            <span className="src-fl src-fl--gap">Signal</span>
            <FilterMenu
              ariaLabel="Filter by AI Trust Signal"
              value={signal}
              options={signalFilterOptions}
              onChange={setSignal}
            />

            <div className="src-search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.5" y2="16.5" />
              </svg>
              <Input
                size="sm"
                type="search"
                placeholder="Filter by domain"
                aria-label="Filter by domain"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <Segmented
              ariaLabel="Table view"
              size="sm"
              value={grouped ? "grouped" : "flat"}
              onChange={(v) => setGrouped(v === "grouped")}
              options={[
                { value: "grouped", label: "Grouped" },
                { value: "flat", label: "Flat" },
              ]}
            />
          </div>

          <div className="src-count" aria-live="polite">
            Showing <strong>{rows.length}</strong> of {sources.length} sources
            {anyFilter && (
              <button type="button" className="src-clear" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>

          <SourcesTable
            rows={rows}
            grouped={grouped}
            expanded={expanded}
            defaultExpanded={defaultExpanded}
            onToggleGroup={toggleGroup}
            onOpenSource={setOpenId}
          />
        </div>
      )}

      <SourceDrawer reportId={numericReportId} row={openRow} onClose={closeDrawer} />
    </div>
  );
}

export default SourcesPage;
